/**
 * Cache invalidation configuration for the generated API
 *
 * This file configures RTK Query cache tags for proper data invalidation
 * when mutations occur.
 */

import { generatedApi } from './generatedApi';
import Tag from './tag';

generatedApi.enhanceEndpoints({
  endpoints: {
    // =========================================================================
    // CAMPAIGN ENDPOINTS
    // =========================================================================
    campaignsRetrieve: {
      providesTags: (_result: any, _error: any, args: any) => {
        return [{ type: Tag.CAMPAIGN, id: args.id }];
      },
    },
    campaignsList: {
      providesTags: (result: any) => {
        const campaigns = Array.isArray(result) ? result : result?.results || [];
        return [
          ...campaigns.map((c: any) => ({ type: Tag.CAMPAIGN as const, id: c.id })),
          { type: Tag.CAMPAIGN as const },
        ];
      },
    },
    campaignsCreate: {
      invalidatesTags: () => {
        return [{ type: Tag.CAMPAIGN }, { type: Tag.BILLING }, { type: Tag.TRANSACTIONS }];
      },
    },
    campaignsPartialUpdate: {
      invalidatesTags: (_result: any, _error: any, args: any) => {
        return [{ type: Tag.CAMPAIGN, id: args.id }, { type: Tag.BILLING }, { type: Tag.TRANSACTIONS }];
      },
    },
    campaignsUpdate: {
      invalidatesTags: (_result: any, _error: any, args: any) => {
        return [{ type: Tag.CAMPAIGN, id: args.id }, { type: Tag.BILLING }, { type: Tag.TRANSACTIONS }];
      },
    },
    campaignsDestroy: {
      invalidatesTags: (_result: any, _error: any, args: any) => {
        return [{ type: Tag.CAMPAIGN, id: args.id }, { type: Tag.BILLING }, { type: Tag.TRANSACTIONS }];
      },
    },
    campaignsStartCreate: {
      invalidatesTags: (_result: any, _error: any, args: any) => {
        return [{ type: Tag.CAMPAIGN, id: args.id }];
      },
    },
    campaignsStopCreate: {
      invalidatesTags: (_result: any, _error: any, args: any) => {
        return [{ type: Tag.CAMPAIGN, id: args.id }];
      },
    },

    // =========================================================================
    // USER ENDPOINTS
    // =========================================================================
    usersMeRetrieve: {
      providesTags: () => {
        return [{ type: Tag.USER }];
      },
    },
    usersMePartialUpdate: {
      invalidatesTags: () => {
        return [{ type: Tag.USER }];
      },
    },
    authConfirmEmailCreate: {
      invalidatesTags: () => {
        return [{ type: Tag.USER }];
      },
    },

    // =========================================================================
    // BILLING ENDPOINTS
    // =========================================================================
    billingBalanceRetrieve: {
      providesTags: () => {
        return [{ type: Tag.BILLING }];
      },
    },
    billingTransactionsRetrieve: {
      providesTags: () => {
        return [{ type: Tag.TRANSACTIONS }];
      },
    },
    billingSubscriptionCancelCreate: {
      invalidatesTags: () => {
        return [
          { type: Tag.SUBSCRIPTIONS },
          { type: Tag.USER },
          { type: Tag.PRICES },
          { type: Tag.PACKAGES },
          { type: Tag.BILLING },
        ];
      },
    },
    billingCheckoutCreate: {
      invalidatesTags: () => {
        return [{ type: Tag.BILLING }, { type: Tag.TRANSACTIONS }, { type: Tag.SUBSCRIPTIONS }];
      },
    },
    billingAddTokensCreate: {
      invalidatesTags: () => {
        return [{ type: Tag.BILLING }, { type: Tag.TRANSACTIONS }];
      },
    },

    // =========================================================================
    // SUBSCRIPTION ENDPOINTS
    // =========================================================================
    subscriptionSubscriptionRetrieve: {
      providesTags: () => {
        return [{ type: Tag.SUBSCRIPTIONS }];
      },
    },
    subscriptionPricesRetrieve: {
      providesTags: () => {
        return [{ type: Tag.PRICES }];
      },
    },
    subscriptionPackagesRetrieve: {
      providesTags: () => {
        return [{ type: Tag.PACKAGES }];
      },
    },

    // =========================================================================
    // LEADS ENDPOINTS
    // =========================================================================
    leadsList: {
      providesTags: (result: any) => {
        const leads = Array.isArray(result) ? result : result?.results || [];
        return [
          ...leads.map((l: any) => ({ type: Tag.DB_LEADS as const, id: l.id })),
          { type: Tag.DB_LEADS as const },
        ];
      },
    },
    leadsCreate: {
      invalidatesTags: () => {
        return [{ type: Tag.DB_LEADS }];
      },
    },
    leadsPartialUpdate: {
      invalidatesTags: (_result: any, _error: any, args: any) => {
        return [{ type: Tag.DB_LEADS, id: args.id }];
      },
    },
    leadsDestroy: {
      invalidatesTags: (_result: any, _error: any, args: any) => {
        return [{ type: Tag.DB_LEADS, id: args.id }];
      },
    },
    leadsDatabaseRetrieve: {
      providesTags: (result: any) => {
        const leads = result?.data || result?.results || [];
        return [
          ...leads.map((l: any) => ({ type: Tag.DB_LEADS as const, id: l.id || l.leads_id })),
          { type: Tag.DB_LEADS as const },
        ];
      },
    },
    leadsMineRetrieve: {
      providesTags: () => {
        return [{ type: Tag.DB_LEADS }];
      },
    },

    // =========================================================================
    // MARKETPLACE ENDPOINTS
    // =========================================================================
    marketplacePurchaseCreate: {
      invalidatesTags: () => {
        return [
          { type: Tag.DB_LEADS },
          { type: Tag.BILLING },
          { type: Tag.TRANSACTIONS },
          { type: Tag.PURCHASED_LEADS },
        ];
      },
    },
    marketplacePurchasedRetrieve: {
      providesTags: () => {
        return [{ type: Tag.PURCHASED_LEADS }];
      },
    },

    // =========================================================================
    // CAMPAIGN FILTERS & SOURCES
    // =========================================================================
    campaignFiltersRetrieve: {
      providesTags: (_result: any, _error: any, args: any) => {
        return [{ type: Tag.CAMPAIGN_FILTERS, id: args.campaignTypeId }];
      },
    },
    campaignSourcesRetrieve: {
      providesTags: (_result: any, _error: any, args: any) => {
        return [{ type: Tag.CAMPAIGN_FILTERS, id: args.campaignTypeId }];
      },
    },

    // =========================================================================
    // SCRAPER TASKS
    // =========================================================================
    scrapersTasksCreate: {
      invalidatesTags: () => {
        return [{ type: Tag.CAMPAIGN }];
      },
    },
    scrapersTasksCancelCreate: {
      invalidatesTags: (_result: any, _error: any, args: any) => {
        return [{ type: Tag.CAMPAIGN }];
      },
    },

    // =========================================================================
    // EMAIL CAMPAIGNS (from Django, not email microservice)
    // =========================================================================
    emailCampaignsEmailCampaignsList: {
      providesTags: (result: any) => {
        const campaigns = Array.isArray(result) ? result : result?.results || [];
        return [
          ...campaigns.map((c: any) => ({ type: Tag.CAMPAIGN as const, id: `email-${c.id}` })),
          { type: Tag.CAMPAIGN as const },
        ];
      },
    },
    emailCampaignsEmailCampaignsCreate: {
      invalidatesTags: () => {
        return [{ type: Tag.CAMPAIGN }, { type: Tag.BILLING }];
      },
    },

    // =========================================================================
    // ORGANIZATIONS
    // =========================================================================
    organizationsList: {
      providesTags: (result: any) => {
        const orgs = Array.isArray(result) ? result : result?.results || [];
        return orgs.map((o: any) => ({ type: Tag.USER as const, id: `org-${o.id}` }));
      },
    },
    organizationsCurrentRetrieve: {
      providesTags: () => {
        return [{ type: Tag.USER }];
      },
    },
  },
});
